import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import toast from "react-hot-toast";
//
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import {
    useFetchAllUserWalletsMutation,
    useUpdateSchemeWalletStatusMutation,
} from "../../../features/tenant/schemeWallet/schemeWalletApi";

const inputClass =
    "block w-full pl-2 pr-3 border border-gray-300 rounded-lg bg-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors";

// scheme_wallet.status: 0 deleted, 1 active, 2 inactive, 3 freezed
const STATUS_META = {
    0: { label: "Deleted", cls: "bg-red-100 text-red-700" },
    1: { label: "Active", cls: "bg-green-100 text-green-700" },
    2: { label: "Inactive", cls: "bg-gray-200 text-gray-700" },
    3: { label: "Freezed", cls: "bg-yellow-100 text-yellow-700" },
};

const StatusBadge = ({ status }) => {
    const meta = STATUS_META[Number(status)] || {
        label: String(status ?? "-"),
        cls: "bg-gray-100 text-gray-600",
    };
    return (
        <span
            className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${meta.cls}`}
        >
            {meta.label}
        </span>
    );
};

const SchemeWalletList = () => {
    const params = useParams();
    const location = useLocation();

    const scheme = location.state || {};
    const schemeId = scheme?.id || params?.schemeId;
    const schemeName = scheme?.name || "";

    const initialFilters = {
        scheme: schemeId,
        mobile: "",
        user_id: "",
        user_type_id: "",
        status: "all",
    };

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [request, setRequest] = useState(initialFilters);
    const [allData, setAllData] = useState([]);
    const [paginationData, setPaginationData] = useState({
        total_items: 0,
        current_page: 1,
        limit: 10,
        total_pages: 0,
    });

    const [selectedRows, setSelectedRows] = useState([]);
    const [targetStatus, setTargetStatus] = useState("1");
    // Toggled to force react-data-table to clear its checkbox selection after an action.
    const [clearSelectionToggle, setClearSelectionToggle] = useState(false);

    const [
        fetchAllUserWalletsApi,
        { data: userWalletsData, error: userWalletsError },
    ] = useFetchAllUserWalletsMutation();

    const [updateSchemeWalletStatusApi] = useUpdateSchemeWalletStatusMutation();

    const token = () => sessionStorage.getItem("access_token");
    const tenantId = () =>
        JSON.parse(sessionStorage.getItem("vendorData")).tenant_id;

    const handleSearch = async (body) => {
        toast.loading("Please wait", { id: "wallet-search" });
        await fetchAllUserWalletsApi({
            token: token(),
            slug: tenantId(),
            body,
        });
        toast.dismiss("wallet-search");
    };

    const handlePageChange = (pageNumber) => {
        setPage(pageNumber);
        handleSearch({ ...request, page: pageNumber, limit });
    };

    const handlePerRowsChange = (newPerPage) => {
        setLimit(newPerPage);
        setPage(1);
        handleSearch({ ...request, page: 1, limit: newPerPage });
    };

    const refreshList = () => {
        setSelectedRows([]);
        setClearSelectionToggle((v) => !v);
        handleSearch({ ...request, page, limit });
    };

    const applyStatus = async (applyAll) => {
        if (!schemeId) {
            toast.error("Scheme not identified");
            return;
        }

        const body = applyAll
            ? {
                  status: targetStatus,
                  applyAll: true,
                  scheme_id: schemeId,
                  filters: {
                      mobile: request.mobile,
                      user_id: request.user_id,
                      user_type_id: request.user_type_id,
                      status: request.status,
                  },
              }
            : {
                  status: targetStatus,
                  walletIds: selectedRows.map((r) => r.id),
              };

        if (!applyAll && body.walletIds.length === 0) {
            toast.error("Select at least one wallet");
            return;
        }

        toast.loading("Updating...", { id: "wallet-flip" });
        const res = await updateSchemeWalletStatusApi({
            token: token(),
            slug: tenantId(),
            body,
        });
        toast.dismiss("wallet-flip");

        if (res?.error) {
            toast.error(res.error?.data?.message || "Update failed");
            return;
        }

        const updated = res?.data?.body?.updated ?? 0;
        toast.success(`${updated} wallet(s) updated`);
        refreshList();
    };

    useEffect(() => {
        if (userWalletsData) {
            toast.dismiss("wallet-search");
            setAllData(userWalletsData?.body?.list || []);
            setPaginationData(userWalletsData?.body?.pagination || {});
        }
        if (userWalletsError) {
            toast.dismiss("wallet-search");
            toast.error(userWalletsError?.data?.message || "Something went wrong", {
                id: "userWalletsError",
            });
        }
    }, [userWalletsData, userWalletsError]);

    useEffect(() => {
        handleSearch({ ...initialFilters, page: 1, limit: 10 });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const columns = useMemo(
        () => [
            {
                name: "SNo",
                cell: (row, idx) => (page - 1) * limit + Number(idx + 1),
                width: "70px",
            },
            { name: "Name", cell: (row) => row.name || "-" },
            {
                name: "Mobile",
                cell: (row) => <strong>{row.mobile || "-"}</strong>,
            },
            { name: "User ID", cell: (row) => row.user_id || "-" },
            { name: "Firm Name", cell: (row) => row.firm_name || "-" },
            { name: "User Type", cell: (row) => row.user_type || "-" },
            {
                name: "Status",
                cell: (row) => <StatusBadge status={row.status} />,
                width: "110px",
            },
            { name: "Earned", cell: (row) => row.point_earned ?? "0" },
            { name: "Redeemed", cell: (row) => row.point_redeemed ?? "0" },
            {
                name: "Balance",
                cell: (row) => <strong>{row.point_balance ?? "0"}</strong>,
            },
            { name: "Reserved", cell: (row) => row.point_reserved ?? "0" },
            { name: "Total Scans", cell: (row) => row.total_scans ?? "0" },
            { name: "City", cell: (row) => row.city || "-" },
            { name: "State", cell: (row) => row.state || "-" },
            { name: "District", cell: (row) => row.district || "-" },
        ],
        [page, limit],
    );

    return (
        <>
            <div className="flex w-full bg-gray-200 h-full justify-center">
                <div className="w-full">
                    <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
                        Scheme Wallets{schemeName ? ` — ${schemeName}` : ""}
                        <LinksComponent />
                    </div>

                    <div className="p-3 w-full">
                        {/* Filters */}
                        <div className="p-3 w-full bg-white shadow rounded-lg">
                            <form
                                className="flex flex-row gap-4 w-full items-end justify-center p-2"
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    setPage(1);
                                    handleSearch({ ...request, page: 1, limit });
                                }}
                            >
                                <div className="relative searchin w-full">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Mobile
                                    </label>
                                    <input
                                        type="text"
                                        className={`${inputClass}`}
                                        placeholder="Mobile ..."
                                        value={request.mobile}
                                        onChange={(e) =>
                                            setRequest((p) => ({
                                                ...p,
                                                mobile: e.target.value,
                                            }))
                                        }
                                    />
                                </div>

                                <div className="relative searchin w-full">
                                    <label className="block text-sm font-medium text-gray-700">
                                        User ID
                                    </label>
                                    <input
                                        type="text"
                                        className={`${inputClass}`}
                                        placeholder="User ID ..."
                                        value={request.user_id}
                                        onChange={(e) =>
                                            setRequest((p) => ({
                                                ...p,
                                                user_id: e.target.value,
                                            }))
                                        }
                                    />
                                </div>

                                <div className="space-y-2 w-full">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Status
                                    </label>
                                    <select
                                        className={`${inputClass}`}
                                        value={request.status}
                                        onChange={(e) =>
                                            setRequest((p) => ({
                                                ...p,
                                                status: e.target.value,
                                            }))
                                        }
                                    >
                                        <option value="all">All</option>
                                        <option value="1">Active</option>
                                        <option value="2">Inactive</option>
                                        <option value="3">Freezed</option>
                                        <option value="0">Deleted</option>
                                    </select>
                                </div>

                                <div className="relative searchin">
                                    <button
                                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                                        type="submit"
                                    >
                                        Search
                                    </button>
                                </div>
                            </form>
                        </div>

                        {/* Bulk action bar */}
                        <div className="mt-3 p-3 w-full bg-white shadow rounded-lg flex flex-wrap items-center gap-3">
                            <span className="text-sm font-medium text-gray-700">
                                {selectedRows.length} selected
                            </span>
                            <label className="text-sm font-medium text-gray-700">
                                Set status
                            </label>
                            <select
                                className="border border-gray-300 rounded-lg bg-white text-sm px-2 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={targetStatus}
                                onChange={(e) => setTargetStatus(e.target.value)}
                            >
                                <option value="1">Active</option>
                                <option value="2">Inactive</option>
                                <option value="3">Freezed</option>
                            </select>
                            <button
                                type="button"
                                onClick={() => applyStatus(false)}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
                            >
                                Apply to Selected
                            </button>
                            <button
                                type="button"
                                onClick={() => applyStatus(true)}
                                className="bg-amber-600 hover:bg-amber-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
                            >
                                Apply to All Matching
                            </button>
                        </div>

                        {/* Table */}
                        <div className="mt-3 p-3 w-full bg-white shadow rounded-lg">
                            <DataTableComponent
                                keyField="id"
                                columns={columns}
                                data={allData}
                                totalRows={paginationData?.total_items}
                                handlePerRowsChange={handlePerRowsChange}
                                handlePageChange={handlePageChange}
                                selectableRows={true}
                                onSelectedRowsChange={({ selectedRows }) =>
                                    setSelectedRows(selectedRows)
                                }
                                clearSelectedRows={clearSelectionToggle}
                            >
                                <div className="flex m-2 space-x-2 justify-end"></div>
                            </DataTableComponent>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SchemeWalletList;
